import { Box, Typography } from '@mui/material';
import { useBinanceOrderBook, useBinanceTicker, fmtPrice, type BookLevel } from '../../hooks/useMarketData';

const ROWS = 11;

function Side({ levels, type, onPick }: { levels: BookLevel[]; type: 'bid' | 'ask'; onPick?: (p: number) => void }) {
  const color = type === 'bid' ? '#16c784' : '#ea3943';
  const max = Math.max(1, ...levels.map((l) => l.total));
  return (
    <Box>
      {levels.map((l, i) => (
        <Box
          key={i}
          onClick={() => onPick?.(l.price)}
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'space-between',
            px: 1,
            py: '3px',
            cursor: onPick ? 'pointer' : 'default',
            fontSize: 12,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
          }}
        >
          {/* depth bar */}
          <Box
            sx={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: `${(l.total / max) * 100}%`,
              backgroundColor: `${color}1f`,
            }}
          />
          <Typography className="mono" sx={{ color, fontSize: 12, zIndex: 1 }}>{fmtPrice(l.price)}</Typography>
          <Typography className="mono" sx={{ color: 'text.secondary', fontSize: 12, zIndex: 1 }}>{l.qty.toFixed(4)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Live order book ladder. Clicking a row calls onPick(price) to fill the order ticket. */
export function OrderBook({ symbol, onPick }: { symbol: string; onPick?: (price: number) => void }) {
  const { bids, asks } = useBinanceOrderBook(symbol, ROWS);
  const ticker = useBinanceTicker(symbol);
  const up = (ticker?.changePct ?? 0) >= 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 1, pb: 0.5, color: 'text.secondary', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
        <span>Price (USDT)</span><span>Amount</span>
      </Box>
      {/* asks shown best-at-bottom */}
      <Side levels={[...asks].reverse()} type="ask" onPick={onPick} />
      <Box sx={{ px: 1, py: 1, my: 0.5, borderBlock: '1px solid rgba(255,255,255,0.07)' }}>
        <Typography className="mono" sx={{ fontWeight: 700, fontSize: 18, color: up ? '#16c784' : '#ea3943' }}>
          {fmtPrice(ticker?.price)}
        </Typography>
      </Box>
      <Side levels={bids} type="bid" onPick={onPick} />
    </Box>
  );
}
